import { useEffect, useState, useRef, useCallback } from "react";
import { useIntl } from "react-intl";
import Cookie from "js-cookie";
import authStore from "../../auth/AuthStore";

const MAX_RECONNECT_ATTEMPTS = 10;
const INITIAL_RECONNECT_DELAY = 1000; // 1 second
const MAX_RECONNECT_DELAY = 30000; // 30 seconds

const useWebsocket = () => {
  const intl = useIntl();
  const [socket, setSocket] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const isIntentionalCloseRef = useRef(false);
  const isMountedRef = useRef(true);
  const wasConnectedRef = useRef(false);

  const getWebSocketUrl = () => {
    return (
      (window.location.protocol === "https:" ? "wss://" : "ws://") +
      window.location.host +
      "/backend/ws/chat"
    );
  };

  const connect = useCallback(() => {
    // Don't create new connection if one exists and is connecting/open
    if (
      wsRef.current &&
      (wsRef.current.readyState === WebSocket.CONNECTING ||
        wsRef.current.readyState === WebSocket.OPEN)
    ) {
      console.log("[WS] Connection already exists, skipping");
      return;
    }

    // Don't reconnect if component is unmounted
    if (!isMountedRef.current) {
      return;
    }

    console.log("[WS] Connecting...");
    Cookie.set("pmtl_tk", authStore.getToken());

    const ws = new WebSocket(getWebSocketUrl());
    wsRef.current = ws;

    ws.onopen = function (event) {
      console.log("[WS] Connected to: " + event.currentTarget.url);
      Cookie.remove("pmtl_tk", { path: "/" });
      reconnectAttemptsRef.current = 0; // Reset reconnect attempts on successful connection
      setIsReady(true);
      setSocket(ws);
    };

    ws.onclose = (event) => {
      console.log("[WS] Closed:", event.code, event.reason);
      setIsReady(false);
      setSocket(null);
      wsRef.current = null;

      // Don't reconnect if it was an intentional close or component is unmounted
      if (isIntentionalCloseRef.current || !isMountedRef.current) {
        console.log("[WS] Intentional close, not reconnecting");
        return;
      }

      // Attempt to reconnect with exponential backoff
      if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
        const delay = Math.min(
          INITIAL_RECONNECT_DELAY * Math.pow(2, reconnectAttemptsRef.current),
          MAX_RECONNECT_DELAY
        );

        reconnectAttemptsRef.current++;
        console.log(
          `[WS] Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS})`
        );

        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, delay);
      } else {
        console.error("[WS] Max reconnection attempts reached");
      }
    };

    ws.onerror = (error) => {
      console.error("[WS] Error:", error);
      setIsReady(false);
    };
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    isIntentionalCloseRef.current = false;

    // Initial connection
    connect();

    return () => {
      isMountedRef.current = false;
      isIntentionalCloseRef.current = true;

      // Clear any pending reconnection attempts
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      // Close the WebSocket connection
      if (
        wsRef.current &&
        (wsRef.current.readyState === WebSocket.OPEN ||
          wsRef.current.readyState === WebSocket.CONNECTING)
      ) {
        console.log("[WS] Closing connection on unmount");
        wsRef.current.close();
      }
      wsRef.current = null;
    };
  }, [connect]);

  // Track connection state changes and show notifications
  useEffect(() => {
    if (isReady && !wasConnectedRef.current) {
      // Connection established for the first time
      console.log("[WS] Connection established");
      wasConnectedRef.current = true;
    } else if (isReady && wasConnectedRef.current) {
      // Reconnected after being disconnected
      console.log("[WS] Reconnected");
    } else if (!isReady && wasConnectedRef.current) {
      // Connection lost
      console.log("[WS] Connection lost");
    }
  }, [isReady, intl]);

  return { socket, isReady };
};

export default useWebsocket;
